import React, { useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  NodeTypes,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';

import RoadmapNode, { RoadmapNodeData } from './RoadmapNode';
import { roadmapData } from '../data/roadmapData';
import ErrorBoundary from './ErrorBoundary';
import { trackSectionView } from '../utils/analytics';

// Define custom node types
const nodeTypes: NodeTypes = {
  roadmapNode: RoadmapNode,
};

// Helper function to create main nodes only - moved to component as useMemo
const JobSearchRoadmap: React.FC = () => {
  const navigate = useNavigate();
  
  // Using useMemo to create nodes only when roadmapData changes
  const { initialMainNodes, initialMainEdges } = useMemo(() => {
    const nodes: Node<RoadmapNodeData>[] = [];
    const edges: Edge[] = [];
    
    // Colors for the main nodes - cleaner, more professional gradient themes
    const mainColors = [
      'linear-gradient(135deg, #3c64b1 0%, #5a76b3 100%)', // Professional blue
      'linear-gradient(135deg, #2c6975 0%, #68b2a0 100%)', // Teal/green
      'linear-gradient(135deg, #4b6cb7 0%, #182848 100%)', // Deep blue
      'linear-gradient(135deg, #396afc 0%, #2948ff 100%)', // Bright blue
      'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', // Green
      'linear-gradient(135deg, #536976 0%, #292E49 100%)', // Slate
      'linear-gradient(135deg, #1a2980 0%, #26D0CE 100%)', // Blue/teal
    ];

    // Layout settings
    const mainNodeSpacing = 180;
    
    try {
      // Add Spotify embed node as the first node
      nodes.push({
        id: 'spotify-podcast',
        type: 'roadmapNode',
        position: { x: 250, y: -300 }, // Position it above the first main node
        data: {
          label: 'לחצו לשמוע את הroadmap',
          description: 'האזינו לפודקאסט המתאר את מפת הדרכים לחיפוש עבודה',
          spotifyEmbed: 'https://open.spotify.com/embed/episode/2PRIFFbDL00GTm9bkfke6Q?utm_source=generator',
          color: 'transparent',
          gradient: 'linear-gradient(135deg, #1DB954 0%, #1AA34A 100%)', // Spotify green gradient
          initiallyExpanded: true, // Start with the node expanded
        },
      });
      
      // Create main nodes (vertically arranged)
      roadmapData.nodes.forEach((mainNode, mainIndex) => {
        const mainNodeId = mainNode.id;
        nodes.push({
          id: mainNodeId,
          type: 'roadmapNode',
          position: { x: 250, y: (mainIndex * mainNodeSpacing) + 100 }, // Add extra space to account for the Spotify node
          data: {
            label: mainNode.label,
            description: mainNode.description || 'לחץ כדי לראות את השלבים הבאים',
            videoLink: mainNode.videoLink || undefined,
            tips: mainNode.tips || ['לחץ להצגת שלבי המשנה'],
            color: 'transparent', 
            gradient: mainColors[mainIndex % mainColors.length],
            isMain: true,
            hasChildren: mainNode.children && mainNode.children.length > 0,
            childrenCount: mainNode.children ? mainNode.children.length : 0,
            sectionId: mainNodeId,
          },
        });
        
        // Connect Spotify node to the first main node if this is the first node
        if (mainIndex === 0) {
          edges.push({
            id: `e-spotify-${mainNodeId}`,
            source: 'spotify-podcast',
            target: mainNodeId,
            animated: true,
            style: { 
              stroke: '#1DB954', // Spotify green
              strokeWidth: 2,
              strokeOpacity: 0.7
            },
          });
        }
        
        // Connect main nodes with each other
        if (mainIndex > 0) {
          const prevMainNodeId = roadmapData.nodes[mainIndex - 1].id;
          edges.push({
            id: `e-main-${prevMainNodeId}-${mainNodeId}`,
            source: prevMainNodeId,
            target: mainNodeId,
            animated: true,
            style: { 
              stroke: '#3c64b1', 
              strokeWidth: 2,
              strokeOpacity: 0.6
            },
          });
        }
      });
    } catch (error) {
      console.error("Error creating roadmap nodes:", error);
    }
    
    return { initialMainNodes: nodes, initialMainEdges: edges };
  }, []);
  
  const [nodes, , onNodesChange] = useNodesState(initialMainNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialMainEdges);

  // Handle connections between nodes
  const onConnect = useCallback(
    (connection: Connection) => setEdges((eds) => addEdge(connection, eds)),
    [setEdges],
  );

  // Handle node click to navigate to section detail
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node<RoadmapNodeData>) => {
    // Prevent ReactFlow's default selection behavior
    event.stopPropagation();
    
    if (node.data?.isMain && node.data?.hasChildren && node.data?.sectionId) {
      const sectionId = node.data.sectionId;
      const sectionLabel = node.data.label;
      
      // Track section navigation
      trackSectionView(sectionId, sectionLabel);
      
      // Navigate to detailed section view
      navigate(`/section/${sectionId}`);
    }
  }, [navigate]);

  const paneMaxZoomIn = useCallback(() => {
    document.documentElement.classList.add('react-flow__viewport-zooming');
    setTimeout(() => {
      document.documentElement.classList.remove('react-flow__viewport-zooming');
    }, 500);
  }, []);

  return (
    <ErrorBoundary fallback={<div className="error-message">שגיאה בטעינת המפה. אנא רענן את הדף.</div>}>
      <ReactFlowProvider>
        <div className="roadmap-container" role="application" aria-label="מפת דרכים אינטראקטיבית לחיפוש עבודה">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.5}
            maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
            onPaneClick={() => paneMaxZoomIn()}
            proOptions={{ hideAttribution: true }}
          >
            <Controls showInteractive={false} />
            <Background gap={12} size={1} />
            <Panel position="top-right" className="info-panel">
              <div className="zoom-info">השתמש במקש הגלילה להתקרב או להתרחק</div>
            </Panel>
          </ReactFlow>
        </div>
      </ReactFlowProvider>
    </ErrorBoundary>
  );
};

export default memo(JobSearchRoadmap);